import { useCallback, useEffect, useMemo, useState } from "react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import type { RequestFilter } from "@/pages/dashboard/dashboardTab.const";
import type { InterestRequestTableRow } from "@/pages/interests/components/InterestRequestsTable";
import { notifications } from "@mantine/notifications";

type PreferredPartnerApiRow = {
  recommendationId: string;
  company: {
    id: string;
    gtid: string;
    headquartersCountry: string;
    industryCategory: string[];
    status: string;
  };
};

function toSuggestedTableRows(partners: PreferredPartnerApiRow[]): InterestRequestTableRow[] {
  return partners.map((item) => ({
    rowKind: "suggested" as const,
    listSource: "suggested" as const,
    matchId: item.recommendationId,
    company: {
      id: item.company.id,
      gtid: item.company.gtid,
      headquartersCountry: item.company.headquartersCountry,
      industryCategory: item.company.industryCategory,
      status: item.company.status,
    },
    collaborationStatus: "SUGGESTED",
    isRequester: false,
    requesterPaid: false,
    receiverPaid: false,
    requesterSignedNDA: false,
    receiverSignedNDA: false,
    requesterRole: null,
    expectedPartnerRole: null,
  }));
}

type UsePreferredPartnersOptions = {
  activeRequestFilter?: RequestFilter | null;
};

export function usePreferredPartners(options: UsePreferredPartnersOptions = {}) {
  const { activeRequestFilter = null } = options;

  const [apiRows, setApiRows] = useState<PreferredPartnerApiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  const fetchSuggested = useCallback(async () => {
    try {
      const res = await trpc.collaboration.getPreferredPartners.query();
      setApiRows((res.partners ?? []) as PreferredPartnerApiRow[]);
    } catch (err) {
      handleTrpcError(err);
      setApiRows([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await fetchSuggested();
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchSuggested]);

  useEffect(() => {
    if (activeRequestFilter === "suggested" || activeRequestFilter === "all") {
      void fetchSuggested();
    }
  }, [activeRequestFilter, fetchSuggested]);

  const dismissRecommendation = useCallback(async (recommendationId: string) => {
    setDismissingIds((prev) => new Set(prev).add(recommendationId));
    // Optimistic remove
    setApiRows((prev) => prev.filter((r) => r.recommendationId !== recommendationId));
    try {
      await trpc.collaboration.dismissPreferredPartner.mutate({ recommendationId });
    } catch (err) {
      // Roll back on failure
      handleTrpcError(err);
      void fetchSuggested();
      notifications.show({
        color: "red",
        title: "Could not remove suggestion",
        message: "Please try again.",
      });
    } finally {
      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(recommendationId);
        return next;
      });
    }
  }, [fetchSuggested]);

  const suggestedTableRows = useMemo(() => toSuggestedTableRows(apiRows), [apiRows]);

  return {
    suggestedTableRows,
    suggestedCount: apiRows.length,
    loading,
    dismissingIds,
    dismissRecommendation,
  };
}
